import React, { useCallback, useEffect, useState } from 'react';
import {
    MMDeviceRef,
    MMGroup,
    MMMode,
    deleteMMGroup,
    deleteMMMode,
    listMMGroups,
    listMMModeDevices,
    listMMModes,
} from '../../api/mmode';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import Modal from '../../components/Modal';
import MMGroupEditorModal, { newMMGroupDraft } from '../../components/mmode/MMGroupEditorModal';
import { ArchTags } from '../../components/mmode/archs';
import MMModeEditorModal, { newMMModeDraft } from '../../components/mmode/MMModeEditorModal';
import MMModeMigrateModal from '../../components/mmode/MMModeMigrateModal';
import BatchActions, {
    HeaderCheckbox,
    RowCheckbox,
} from '../../components/BatchActions';
import { useBatchSelect } from '../../utils/useBatchSelect';
import { confirmDialog, alertDialog } from '../../utils/dialog';
import { useCan } from '../../store';
import { useIsMaintainer } from '../../utils/readonlyScope';

// ★设计A (修改清单附录B): 加工模式字典管理页.
// 左 = 加工模式组 (参数真实键, 加工头类型挂此); 右 = 选中组下的模式值 (对外契约, 中文名供参-02).
// 设备引用主维护口在设备管理页; 这里只读反查"被哪些设备引用" (同小贴士引用数思路).
// 权限: 登录即可操作, 写操作统一走审批工单.

function errMsg(e: unknown): string {
    return e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
}

function MModeAdmin() {
    const canCreate = useCan('mm_mode', 'create');
    const canUpdate = useCan('mm_mode', 'update');
    const canDelete = useCan('mm_mode', 'delete');
    const maintainer = useIsMaintainer('mm_mode');
    const [groups, setGroups] = useState<MMGroup[]>([]);
    const [modes, setModes] = useState<MMMode[]>([]);
    const [curGroup, setCurGroup] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // 编辑状态: 组 / 模式值 共用"新建或编辑一行"的弹窗模式
    const [editGroup, setEditGroup] = useState<Partial<MMGroup> | null>(null);
    const [editMode, setEditMode] = useState<Partial<MMMode> | null>(null);
    const [migrating, setMigrating] = useState(false);
    const [refDevices, setRefDevices] = useState<{
        value: string;
        list: MMDeviceRef[];
    } | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [gs, ms] = await Promise.all([
                listMMGroups(true),
                listMMModes('', true),
            ]);
            setGroups(gs);
            setModes(ms);
        } catch (e) {
            setError(errMsg(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // 只读访问者只看启用的分组与模式 (停用项是维护过程态)
    const shownGroups = maintainer ? groups : groups.filter((g) => g.isEnabled);
    const shownModes = (
        curGroup ? modes.filter((m) => m.groupCode === curGroup) : modes
    ).filter((m) => maintainer || m.isEnabled);

    // 批量操作 (2026-09-10): 组与模式值各一套选择 (两个不同资源域, 删除动作也不同)
    const groupSel = useBatchSelect(
        groups.map((g) => g.code),
        String(groups.length),
    );
    const modeSel = useBatchSelect(
        shownModes.map((m) => m.value),
        [curGroup, shownModes.length].join('|'),
    );
    const modeCntByGroup = modes.reduce<Record<string, number>>((acc, m) => {
        acc[m.groupCode] = (acc[m.groupCode] || 0) + 1;
        return acc;
    }, {});

    const removeGroup = async (g: MMGroup) => {
        if (
            !(await confirmDialog(
                `确认删除加工模式组 ${g.nameZh || g.code}? 组下有模式值时会被拒绝.`,
            ))
        )
            return;
        try {
            const res = await deleteMMGroup(g.code);
            await load();
            alertApprovalIfPending(res.message);
        } catch (e) {
            alertDialog(`删除失败: ${errMsg(e)}`);
        }
    };

    const removeMode = async (m: MMMode) => {
        if (!(await confirmDialog(`确认删除模式值 ${m.value}?`))) return;
        try {
            const res = await deleteMMMode(m.value);
            await load();
            alertApprovalIfPending(res.message);
        } catch (e) {
            alertDialog(`删除失败: ${errMsg(e)}`);
        }
    };

    const showRefs = async (m: MMMode) => {
        try {
            const list = await listMMModeDevices(m.value);
            setRefDevices({ value: m.value, list });
        } catch (e) {
            alertDialog(`查询失败: ${errMsg(e)}`);
        }
    };

    return (
        <div className="space-y-5">
            <div>
                <div className="text-base font-semibold">加工模式字典</div>
                <div className="text-xs text-slate-500">
                    加工模式(复用组) = 参数真实键, 同组的多个模式值共享一份参数;
                    模式值 = 对外契约, 中文名用于参数处枚举中文显示
                    (空则显示原值). 设备引用在「设备工况 → 设备管理」维护,
                    此处只读反查.
                </div>
            </div>

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    错误: {error}
                </div>
            )}

            <div className="grid grid-cols-5 gap-4 items-start">
                {/* 左: 加工模式组 */}
                <div className="col-span-2 card p-4">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold">
                            加工模式 / 复用组 ({groups.length})
                        </span>
                        <BatchActions
                            resource="mm_group"
                            entityLabel="模式组"
                            deleteKeyField="code"
                            selected={groupSel.selectedKeys}
                            onClear={groupSel.clear}
                            onDone={load}
                        />
                        <button
                            type="button"
                            className="btn-ghost text-xs"
                            onClick={() => setEditGroup(newMMGroupDraft())}
                            hidden={!canCreate}
                        >
                            + 新建组
                        </button>
                    </div>
                    <div className="space-y-1">
                        <div
                            className={`px-2 py-1.5 rounded cursor-pointer text-sm ${curGroup === '' ? 'bg-brand-50 text-brand-700' : 'hover:bg-slate-50'}`}
                            onClick={() => setCurGroup('')}
                        >
                            全部模式值
                        </div>
                        {shownGroups.map((g) => (
                            <div
                                key={g.code}
                                className={`px-2 py-1.5 rounded cursor-pointer flex items-center gap-2 text-sm ${curGroup === g.code ? 'bg-brand-50 text-brand-700' : 'hover:bg-slate-50'}`}
                                onClick={() => setCurGroup(g.code)}
                            >
                                <span
                                    className="shrink-0"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <RowCheckbox
                                        hidden={!canDelete}
                                        sel={groupSel}
                                        k={g.code}
                                    />
                                </span>
                                <span
                                    className={
                                        g.nameZh ? '' : 'font-mono text-xs'
                                    }
                                >
                                    {g.nameZh || g.code}
                                </span>
                                <ArchTags codes={g.archCodes} />
                                <span className="text-[11px] text-slate-400">
                                    {modeCntByGroup[g.code] || 0} 值
                                </span>
                                {!g.isEnabled && (
                                    <span className="tag bg-slate-100 text-slate-400 text-[10px]">
                                        停用
                                    </span>
                                )}
                                <span className="ml-auto flex gap-2 text-xs">
                                    <button
                                        type="button"
                                        className="text-brand-600 hover:underline"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setEditGroup({ ...g });
                                        }}
                                        hidden={!canUpdate}
                                    >
                                        编辑
                                    </button>
                                    <button
                                        type="button"
                                        className="text-rose-500 hover:underline"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeGroup(g);
                                        }}
                                        hidden={!canDelete}
                                    >
                                        删除
                                    </button>
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 右: 模式值 */}
                <div className="col-span-3 card p-4">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold">
                            模式值
                            {curGroup
                                ? ` · ${groups.find((g) => g.code === curGroup)?.nameZh || curGroup}`
                                : ''}{' '}
                            ({shownModes.length})
                        </span>
                        <BatchActions
                            resource="mm_mode"
                            entityLabel="模式值"
                            deleteKeyField="value"
                            selected={modeSel.selectedKeys}
                            onClear={modeSel.clear}
                            onDone={load}
                        />
                        <button
                            type="button"
                            className="btn-ghost text-xs"
                            onClick={() => setMigrating(true)}
                            disabled={modeSel.selectedKeys.length === 0}
                            title={
                                modeSel.selectedKeys.length === 0
                                    ? '请先在列表里勾选要迁移的模式值'
                                    : '把所选模式值迁移到另一个加工模式'
                            }
                            hidden={!canUpdate}
                        >
                            迁移
                            {modeSel.selectedKeys.length
                                ? ` (${modeSel.selectedKeys.length})`
                                : ''}
                        </button>
                        <button
                            type="button"
                            className="btn-ghost text-xs"
                            onClick={() =>
                                setEditMode(
                                    newMMModeDraft(curGroup || groups[0]?.code || ''),
                                )
                            }
                            hidden={!canCreate}
                        >
                            + 新建模式值
                        </button>
                    </div>
                    {loading ? (
                        <div className="text-center text-slate-400 text-sm py-8">
                            加载中…
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-xs text-slate-500 text-left">
                                    <th className="py-1.5 w-8">
                                        <HeaderCheckbox
                                            hidden={!canDelete && !canUpdate}
                                            sel={modeSel}
                                        />
                                    </th>
                                    <th className="py-1.5">模式值 (对外)</th>
                                    <th>中文名</th>
                                    <th>加工模式</th>
                                    <th>状态</th>
                                    <th className="text-right">操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {shownModes.map((m) => (
                                    <tr
                                        key={m.value}
                                        className="border-t border-slate-100 hover:bg-slate-50"
                                    >
                                        <td className="py-1.5">
                                            <RowCheckbox
                                                hidden={!canDelete && !canUpdate}
                                                sel={modeSel}
                                                k={m.value}
                                            />
                                        </td>
                                        <td className="py-1.5 font-mono text-xs">
                                            {m.value}
                                        </td>
                                        <td>
                                            {m.nameZh || (
                                                <span className="text-slate-300">
                                                    未命名 (显示原值)
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-xs text-slate-500">
                                            {groups.find(
                                                (g) => g.code === m.groupCode,
                                            )?.nameZh || m.groupCode}
                                        </td>
                                        <td>
                                            {m.isEnabled ? (
                                                <span className="tag bg-emerald-50 text-emerald-700 text-[10px]">
                                                    启用
                                                </span>
                                            ) : (
                                                <span className="tag bg-slate-100 text-slate-400 text-[10px]">
                                                    停用
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-right text-xs space-x-2 whitespace-nowrap">
                                            <button
                                                type="button"
                                                className="text-slate-500 hover:underline"
                                                onClick={() => showRefs(m)}
                                            >
                                                引用
                                            </button>
                                            <button
                                                type="button"
                                                className="text-brand-600 hover:underline"
                                                onClick={() =>
                                                    setEditMode({ ...m })
                                                }
                                                hidden={!canUpdate}
                                            >
                                                编辑
                                            </button>
                                            <button
                                                type="button"
                                                className="text-rose-500 hover:underline"
                                                onClick={() => removeMode(m)}
                                                hidden={!canDelete}
                                            >
                                                删除
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {shownModes.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={6}
                                            className="py-6 text-center text-slate-400 text-xs"
                                        >
                                            该组下暂无模式值
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* 组编辑弹窗 */}
            {editGroup && (
                <MMGroupEditorModal
                    group={editGroup}
                    setGroup={setEditGroup}
                    onClose={() => setEditGroup(null)}
                    onSaved={() => {
                        setEditGroup(null);
                        load();
                    }}
                    canSave={canUpdate}
                />
            )}

            {/* 模式值编辑弹窗 */}
            {editMode && (
                <MMModeEditorModal
                    mode={editMode}
                    setMode={setEditMode}
                    groups={groups}
                    onClose={() => setEditMode(null)}
                    onSaved={() => {
                        setEditMode(null);
                        load();
                    }}
                    canSave={canUpdate}
                />
            )}

            {/* 模式值迁移弹窗 */}
            {migrating && (
                <MMModeMigrateModal
                    modes={modes.filter((m) =>
                        modeSel.selectedKeys.includes(m.value),
                    )}
                    groups={groups}
                    onClose={() => setMigrating(false)}
                    onDone={async () => {
                        setMigrating(false);
                        modeSel.clear();
                        await load();
                    }}
                />
            )}

            {/* 引用反查弹窗 */}
            {refDevices && (
                <Modal
                    open
                    title={`引用「${refDevices.value}」的设备 · ${refDevices.list.length} 台`}
                    onClose={() => setRefDevices(null)}
                >
                    {refDevices.list.length === 0 ? (
                        <div className="text-sm text-slate-400 py-4 text-center">
                            暂无设备引用
                        </div>
                    ) : (
                        <div className="space-y-1 max-h-80 overflow-auto">
                            {refDevices.list.map((r) => (
                                <div
                                    key={r.id}
                                    className="flex items-center gap-2 text-sm border border-slate-100 rounded px-3 py-1.5"
                                >
                                    <span className="font-mono text-xs">
                                        {r.deviceKey}
                                    </span>
                                    {r.aliasZh && (
                                        <span className="tag bg-amber-50 text-amber-700 text-[10px]">
                                            别名: {r.aliasZh}
                                        </span>
                                    )}
                                    {!r.enabled && (
                                        <span className="tag bg-slate-100 text-slate-400 text-[10px]">
                                            停用
                                        </span>
                                    )}
                                    {(r.speedMin != null ||
                                        r.speedMax != null) && (
                                        <span className="text-[11px] text-slate-400">
                                            速度 {r.speedMin ?? '—'}~
                                            {r.speedMax ?? '—'}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </Modal>
            )}
        </div>
    );
}

export default MModeAdmin;
